/**
 * Chart → PNG (roadmap RP-7: "charts export as PNG").
 *
 * recharts draws SVG, so a PNG is the chart's SVG serialised, painted onto a
 * canvas at 2× for crisp text, and handed to the browser as a download. The
 * chart's colours are already concrete hex (chartTheme resolves tokens at
 * render time precisely so a detached document paints them), and the ground
 * is painted from the same token so the PNG is never transparent.
 */

import { resolveToken } from '../../../../components/ds/chartTheme';

/** A title as a file name: "By status" → "by-status". */
export function slugifyFilename(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'chart';
}

export interface ChartPngOptions {
  /** Device-pixel multiplier. 2 is crisp on every screen; 1 is smaller. */
  scale?: number;
  /** CSS colour for the ground. Defaults to the raised surface token. */
  background?: string;
}

function findSvg(container: HTMLElement): SVGSVGElement | null {
  return (
    container.querySelector<SVGSVGElement>('svg.recharts-surface') ??
    container.querySelector<SVGSVGElement>('svg')
  );
}

/** Serialise a chart's SVG to a PNG blob. Resolves null when there is no SVG to paint. */
export async function chartToPngBlob(container: HTMLElement, opts: ChartPngOptions = {}): Promise<Blob | null> {
  const svg = findSvg(container);
  if (!svg) return null;
  const scale = opts.scale ?? 2;
  const rect = svg.getBoundingClientRect();
  const width = Math.max(1, Math.round(rect.width || Number(svg.getAttribute('width')) || 600));
  const height = Math.max(1, Math.round(rect.height || Number(svg.getAttribute('height')) || 300));

  const clone = svg.cloneNode(true) as SVGSVGElement;
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  clone.setAttribute('width', String(width));
  clone.setAttribute('height', String(height));
  // Tick labels and legends are mono per the chart rules; name the stack so a
  // detached render still gets a monospace face when the webfont is not loaded.
  const style = document.createElementNS('http://www.w3.org/2000/svg', 'style');
  style.textContent = 'text { font-family: "JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace; }';
  clone.insertBefore(style, clone.firstChild);

  const xml = new XMLSerializer().serializeToString(clone);
  const svgUrl = URL.createObjectURL(new Blob([xml], { type: 'image/svg+xml;charset=utf-8' }));
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('The chart could not be rendered as an image.'));
      image.src = svgUrl;
    });
    const canvas = document.createElement('canvas');
    canvas.width = width * scale;
    canvas.height = height * scale;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.fillStyle = opts.background ?? resolveToken('--bg-raised', container);
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.scale(scale, scale);
    ctx.drawImage(img, 0, 0, width, height);
    return await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  } finally {
    URL.revokeObjectURL(svgUrl);
  }
}

/** Download a chart as `<filename>.png`. Returns false when the container holds no chart. */
export async function downloadChartPng(container: HTMLElement, filename: string, opts?: ChartPngOptions): Promise<boolean> {
  const blob = await chartToPngBlob(container, opts);
  if (!blob) return false;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.png') ? filename : `${filename}.png`;
  a.click();
  URL.revokeObjectURL(url);
  return true;
}
